import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getUsers, createUser, updateUser, deleteUser } from '../api/userApi';
import { getAllRoles } from '../api/roleApi';
import { toast } from 'react-toastify';
import { 
  Users, 
  PlusCircle, 
  Edit, 
  Trash2, 
  Search, 
  X, 
  ChevronDown, 
  ChevronUp,
  RefreshCw,
  Shield
} from 'lucide-react';
import { getRoleDisplayName, getRoleColorClass } from '../utils/roleUtils';

// Kullanıcı ekleme/düzenleme modalı
const UserFormModal = ({ user, onClose, onSubmit, isEditing }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role_id: '',
    email: '',
    full_name: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  
  // Rolleri yükle
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await getAllRoles();
        setRoles(response.data || []);
      } catch (error) {
        console.error('Roller yüklenirken hata:', error);
        toast.error('Roller yüklenirken hata oluştu');
      } finally {
        setRolesLoading(false);
      }
    };
    
    fetchRoles();
  }, []);
  
  // Düzenleme modunda form verilerini doldur
  useEffect(() => {
    if (isEditing && user) {
      setFormData({
        username: user.username || '',
        password: '', // Şifre alanını boş bırak (değiştirilmezse mevcut şifre korunur)
        role_id: user.role_id || '',
        email: user.email || '',
        full_name: user.full_name || '',
        is_active: user.is_active === undefined ? true : user.is_active
      });
    }
  }, [isEditing, user]);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validasyonu
    if (!formData.username || (!isEditing && !formData.password) || !formData.role_id) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }
    
    // Kullanıcı adı formatını kontrol et
    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      toast.error('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir');
      return;
    }
    
    setLoading(true);
    
    try {
      // Şifre boşsa ve düzenleme modundaysa, şifreyi objeden çıkar
      const userData = { ...formData };
      if (isEditing && !userData.password) {
        delete userData.password;
      }
      
      await onSubmit(userData);
      onClose();
    } catch (error) {
      toast.error(`İşlem başarısız: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {/* Kullanıcı Adı */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kullanıcı Adı *
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={isEditing} // Düzenleme modunda kullanıcı adı değiştirilemez
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
              />
              {isEditing && (
                <p className="mt-1 text-xs text-gray-500">Kullanıcı adı değiştirilemez</p>
              )}
            </div>
            
            {/* Şifre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Şifre {isEditing ? '' : '*'}
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!isEditing}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {isEditing && (
                <p className="mt-1 text-xs text-gray-500">Sadece değiştirmek istiyorsanız doldurun</p>
              )}
            </div>
            
            {/* Rol */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yetki Rolü *
              </label>
              {rolesLoading ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50">
                  Roller yükleniyor...
                </div>
              ) : (
                <select
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Rol Seçin</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {/* Tam Ad */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tam Ad
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* E-posta */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-posta
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {/* Aktif/Pasif */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Hesap Aktif
              </label>
            </div>
          </div>
          
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed"
            >
              {loading ? 'İşleniyor...' : isEditing ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Silme onay modalı
const DeleteConfirmModal = ({ user, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  
  const handleDelete = async () => {
    setLoading(true);
    
    try {
      await onConfirm(user.id);
      toast.success('Kullanıcı başarıyla silindi');
      onClose();
    } catch (error) {
      toast.error(`Silme işlemi başarısız: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Kullanıcıyı Sil</h3>
        </div>
        
        <div className="p-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <Trash2 className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-center text-gray-900 mb-2">
            Kullanıcıyı silmek istediğinize emin misiniz?
          </h3>
          <p className="text-sm text-center text-gray-500 mb-1">
            <strong>{user.username}</strong> kullanıcısı kalıcı olarak silinecektir.
          </p>
          <p className="text-xs text-center text-red-500">
            Bu işlem geri alınamaz!
          </p>
        </div>
        
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 mr-3"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-red-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Siliniyor...' : 'Sil'}
          </button>
        </div>
      </div>
    </div>
  );
};

const UsersPage = () => {
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  
  const [sortConfig, setSortConfig] = useState({
    key: 'username',
    direction: 'asc'
  });
  
  // Modallar
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Kullanıcıları yükle
  useEffect(() => {
    fetchUsers();
  }, []);
  
  // Arama ve filtreleme
  useEffect(() => {
    if (!users) return;
    
    let filtered = [...users];
    
    // Arama
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.username.toLowerCase().includes(search) ||
          (user.full_name && user.full_name.toLowerCase().includes(search)) ||
          (user.email && user.email.toLowerCase().includes(search))
      );
    }
    
    // Rol filtresi
    if (roleFilter) {
      filtered = filtered.filter(user => user.role_id === roleFilter);
    }
    
    // Sıralama
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, sortConfig]);
  
  // Kullanıcıları getir
  const fetchUsers = async () => {
    setRefreshing(true);
    
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      toast.error(`Kullanıcılar yüklenirken hata: ${error.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Sıralama ayarla
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };
  
  // Sıralama ikonları
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronDown size={16} className="ml-1 text-gray-400" />;
    }
    
    return sortConfig.direction === 'asc' ? (
      <ChevronUp size={16} className="ml-1 text-primary-500" />
    ) : (
      <ChevronDown size={16} className="ml-1 text-primary-500" />
    );
  };
  
  // Yeni kullanıcı ekle
  const handleAddUser = async (userData) => {
    try {
      const newUser = await createUser(userData);
      setUsers([...users, newUser]);
      toast.success('Kullanıcı başarıyla eklendi');
      return newUser;
    } catch (error) {
      throw error;
    }
  };
  
  // Kullanıcı güncelle
  const handleUpdateUser = async (userData) => {
    try {
      const updatedUser = await updateUser(selectedUser.id, userData);
      setUsers(users.map(user => user.id === selectedUser.id ? updatedUser : user));
      toast.success('Kullanıcı başarıyla güncellendi');
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };
  
  // Kullanıcı sil
  const handleDeleteUser = async (id) => {
    try {
      await deleteUser(id);
      setUsers(users.filter(user => user.id !== id));
      return true;
    } catch (error) {
      throw error;
    }
  };
  
  // Rolleri yükle
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await getAllRoles();
        setRoles(response.data || []);
      } catch (error) {
        console.error('Roller yüklenirken hata:', error);
        toast.error('Roller yüklenirken hata oluştu');
      } finally {
        setRolesLoading(false);
      }
    };
    
    fetchRoles();
  }, []);
  
  // Yükleme durumu
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div className="py-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Kullanıcı Yönetimi</h1>
          <p className="text-gray-600">Sistem kullanıcılarını yönetin ve yetkilendirin.</p>
        </div>
        
        <button
          onClick={() => setShowAddModal(true)}
          className="mt-4 sm:mt-0 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start"
        >
          <PlusCircle size={16} className="mr-1" />
          Yeni Kullanıcı Ekle
        </button>
      </div>
      
      {/* Arama ve Filtreleme */}
      <div className="mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Arama */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Kullanıcı adı, e-posta veya isim ara..."
                className="pl-10 w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            {/* Rol Filtresi */}
            <div>
              <select
                className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                disabled={rolesLoading}
              >
                <option value="">Tüm Roller</option>
                {rolesLoading ? (
                  <option disabled>Roller yükleniyor...</option>
                ) : (
                  roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.display_name}
                    </option>
                  ))
                )}
              </select>
            </div>
            
            {/* Yenile Butonu */}
            <div className="flex justify-end">
              <button
                onClick={fetchUsers}
                disabled={refreshing}
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-md flex items-center"
              >
                <RefreshCw size={16} className={`mr-1 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Yenileniyor...' : 'Yenile'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Kullanıcı Tablosu */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('username')}
                >
                  <div className="flex items-center">
                    Kullanıcı Adı {getSortIcon('username')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('full_name')}
                >
                  <div className="flex items-center">
                    İsim Soyisim {getSortIcon('full_name')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('role_id')}
                >
                  <div className="flex items-center">
                    Yetki {getSortIcon('role_id')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    E-posta {getSortIcon('email')}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('is_active')}
                >
                  <div className="flex items-center">
                    Durum {getSortIcon('is_active')}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.username}
                      {currentUser.id === user.id && (
                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                          Siz
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.full_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleColorClass(user.role_id)}`}>
                        {getRoleDisplayName(user.role_id)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {/* Düzenle */}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowEditModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Düzenle"
                        >
                          <Edit size={18} />
                        </button>
                        
                        {/* Sil */}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowDeleteModal(true);
                          }}
                          disabled={currentUser.id === user.id} // Kendini silmeyi engelle
                          className={`${
                            currentUser.id === user.id
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-red-600 hover:text-red-900'
                          }`}
                          title={currentUser.id === user.id ? 'Kendinizi silemezsiniz' : 'Sil'}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    {searchTerm || roleFilter ? (
                      <div>
                        <p className="text-lg font-medium">Sonuç bulunamadı</p>
                        <p className="text-sm mt-1">
                          Arama kriterlerinize uygun kullanıcı bulunamadı. Lütfen filtreleri temizleyin veya başka bir arama yapın.
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Shield size={48} className="text-gray-300 mb-3" />
                        <p className="text-lg font-medium">Henüz kullanıcı bulunmuyor</p>
                        <p className="text-sm mt-1 mb-3">
                          Sisteme yeni kullanıcılar ekleyerek başlayın.
                        </p>
                        <button
                          onClick={() => setShowAddModal(true)}
                          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center"
                        >
                          <PlusCircle size={16} className="mr-1" />
                          Yeni Kullanıcı Ekle
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Modallar */}
      {showAddModal && (
        <UserFormModal
          user={null}
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddUser}
          isEditing={false}
        />
      )}
      
      {showEditModal && selectedUser && (
        <UserFormModal
          user={selectedUser}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateUser}
          isEditing={true}
        />
      )}
      
      {showDeleteModal && selectedUser && (
        <DeleteConfirmModal
          user={selectedUser}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteUser}
        />
      )}
    </div>
  );
};

export default UsersPage;